import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  LuBot,
  LuFileText,
  LuLock,
  LuMaximize2,
  LuMic,
  LuMicOff,
  LuMinimize2,
  LuRadio,
  LuRefreshCw,
  LuSend,
  LuSparkles,
  LuSquare,
  LuMonitor,
  LuDownload,
} from "react-icons/lu";
import { Btn, Spinner, Skel, Note, Dialog } from "../components/Ui";
import { setShowPro } from "../../store/actions/actions";
import "./MockInterview.css";

/* Endpoints — identical to legacy MockInterview.js. The resume document name
   (fileResume) is loaded app-wide via send_resume_details_front_end_v2 in the
   shell bootstrap, exactly as the legacy header did. */
const GPT_API = "https://us-east1-foursssolutions.cloudfunctions.net/audio_to_text_response_gpt_v2";
const GPT_STREAM_API =
  "https://us-east1-foursssolutions.cloudfunctions.net/audio_to_text_response_gpt_streaming_v2";

/* When the coach classifies your turn as "candidate_speaking" (you gave an answer
   rather than asking it a question) the backend replies "No need for any answer."
   Showing that verbatim — or silently deleting the turn (old prune behaviour) —
   made typed answers vanish (Pavithra Mock #1). Keep the turn and show a useful
   line instead. */
const NO_NEED = "No need for any answer.";
const COACH_ACK =
  "Got it — noted. Ask me an interview question whenever you're ready and I'll answer it for you.";
function normalizeReply(text) {
  const s = String(text || "").replace(/\s*\}+\s*$/, "").trim();
  return s === NO_NEED || s === "" ? COACH_ACK : text;
}

const SUGGESTIONS = [
  "Can you briefly describe your recent project?",
  "What tools or technologies did you use in your recent project?",
  "Why do you think you are a good fit for this job?",
  "What led you to explore new job opportunities?",
];

/* Auto co-pilot recognizers (carried over from legacy AutoLanguageDetector) */
const AUTO_LANGS = ["en-US", "en-IN"];

/* Beautifier carried over verbatim from legacy handleSend */
function beautifyStreamText(text) {
  const formattingRules = [
    { pattern: /(\d+\.\s\*\*.+?\*\*)/g, replaceWith: "\n$1" },
    {
      pattern: /(In my experience,|Java development offers several benefits, including)/g,
      replaceWith: "\n\n$1",
    },
    { pattern: /\s{2,}/g, replaceWith: " " },
    { pattern: /: \d+\.\s/g, replaceWith: ":\n  " },
  ];
  formattingRules.forEach((rule) => {
    text = text.replace(rule.pattern, rule.replaceWith);
  });
  return text.trim();
}

/* ---------- desktop companion download (hosted on GCS) ---------- */
const DESKTOP_APP_URL =
  "https://storage.googleapis.com/careersavvy-downloads/CareerSavvy-Interview-Companion-1.0.0-win-x64.zip";

export function DesktopAppCard() {
  return (
    <div className="mi-desktop">
      <span className="mi-desktop-ic">
        <LuMonitor size={18} />
      </span>
      <div className="grow">
        <div className="mi-desktop-title">Practice beside your video call</div>
        <p className="t-sm mt-1">
          The Windows companion app keeps your coach in a slim always-on-top rail next to Zoom, Teams, or Meet.
          Same login, same AI coach. It stays visible on screen shares.
        </p>
      </div>
      <a className="btn btn-pill btn-mint" href={DESKTOP_APP_URL} download>
        <LuDownload size={15} /> Download for Windows
      </a>
    </div>
  );
}

export default function MockInterview() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userEmail = useSelector((state) => state.auth.auth.email);
  const { apiTokenReady, fileResume, featuresToBlock = [] } = useSelector((state) => state.profile);

  const blocked = featuresToBlock.includes("block_mockinterview");

  /* ---------- state machine carried over from legacy ---------- */
  const [loaderAnimation, setLoaderAnimation] = useState(true);
  const [GPTsession_id, setGPTSession_id] = useState("");
  const [message, setMessage] = useState("");
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [isListening, setIsListening] = useState(false);
  /* Index of the turn currently being streamed. Set from prev.length inside the
     functional update so it's always the real appended slot — the old
     `indexCounter` state was stale across turns, so answers overwrote earlier
     turns / vanished / looked role-swapped (Pavithra Mock #1/#2/#4). All three
     stream branches (chunk / corrected_text / gpt_response) write to this ref,
     not indexCounter, which is now only a vestigial counter. */
  const turnIndexRef = useRef(0);
  const [aiChatSuggestions, setAiChatSuggestions] = useState(false);
  const [responseLoading, setResponseLoading] = useState(false);
  const [speakInitialise, setSpeakInitialise] = useState(false);
  const [speechText, setSpeechText] = useState("");
  const [voiceText, setVoiceText] = useState("");
  const [voiceSpeech, setVoiceSpeech] = useState(false);
  const [manualCoPilot, setManualCoPilot] = useState(false);
  const [hideQuestion, setHideQuestion] = useState(false);
  const [copilotShown, setCopilotShown] = useState(false);
  const [autoPilotShown, setAutoPilotShown] = useState(false);
  const [autoListening, setAutoListening] = useState(false);
  const [sessionError, setSessionError] = useState("");
  const [sessionWarming, setSessionWarming] = useState(false);
  const [sendError, setSendError] = useState("");
  const [recSeconds, setRecSeconds] = useState(0);
  /* presentation-only full-screen toggle — never touches session/chat state */
  const [expanded, setExpanded] = useState(false);

  const messageContainerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micMeterStreamRef = useRef(null);
  const recogRef = useRef([]);
  /* auto-scroll bookkeeping: legacy scrolled unconditionally on every chunk;
     we keep that cadence but respect a user who scrolled up to read history. */
  const stickToBottomRef = useRef(true);
  const prevTurnCountRef = useRef(0);

  const speechSupported =
    typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  const { transcript, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();

  function clearChats() {
    setInterviewQuestions([]);
    turnIndexRef.current = 0;
  }

  function speakNowShower() {
    if (autoPilotShown === false) {
      setSpeakInitialise(true);
      setHideQuestion(true);
    }
  }

  /* Legacy MockInterview.js: messageContainerRef + scrollTop=scrollHeight, called
     via setTimeout(…,100) on every stream chunk / final gpt_response and from an
     effect on [interviewQuestions, voiceSpeech]. Same cadence here, with a
     near-bottom guard (~120px) so a user reading history isn't yanked down. */
  const handleThreadScroll = () => {
    const el = messageContainerRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const scrollToBottom = (force = false, behavior = "auto") => {
    const el = messageContainerRef.current;
    if (!el) return;
    if (!force && !stickToBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  /* ---------- session bootstrap (payload identical to legacy loadSession) ----------
     The GPT function runs at min-instances 0 (cost policy) so the first hit
     after idle regularly times out while the instance cold-starts. Retry with
     backoff and show a "warming up" state instead of an instant error. */
  const loadSession = async (attempt = 0) => {
    const text = "I'm going to start my mock interview and prepare for it ?";
    if (text.trim() === "") return;
    setSessionError("");
    const payload = {
      transcribed_text: text,
      resume_load: true,
      email_id: userEmail,
      document_name: fileResume,
      session_id: "",
    };
    try {
      const response = await fetch(GPT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (text !== data.response) {
        // opening interviewer question — the priming prompt is internal, so no "You" bubble
        setInterviewQuestions((prev) => [...prev, { question: "", answer: data.response }]);
      }
      setGPTSession_id(data.session_id);
      setSessionWarming(false);
      setMessage("");
      setAiChatSuggestions(true);
      setTimeout(() => {
        setAiChatSuggestions(false);
      }, 5000);
    } catch (error) {
      console.error("gpt first response Error:", error);
      if (attempt < 2) {
        setSessionWarming(true);
        setTimeout(() => loadSession(attempt + 1), attempt === 0 ? 2500 : 7000);
        return;
      }
      setSessionWarming(false);
      setSessionError("We could not start your mock interview session.");
      setLoaderAnimation(false);
    }
  };

  useEffect(() => {
    if (blocked) {
      setLoaderAnimation(false);
      return;
    }
    if (userEmail !== "" && apiTokenReady === true) {
      if (fileResume) {
        loadSession();
        setTimeout(() => {
          setLoaderAnimation(false);
        }, 3500);
      } else {
        setLoaderAnimation(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenReady, fileResume]);

  /* ---------- suggestions (payload identical to legacy) ---------- */
  const chatSuggestionsClick = async (text) => {
    if (text.trim() === "") return;
    setResponseLoading(true);
    setAiChatSuggestions(false);
    const Suggestionspayload = {
      transcribed_text: text,
      resume_load: true,
      email_id: userEmail,
      document_name: fileResume,
      session_id: GPTsession_id,
    };
    try {
      const response = await fetch(GPT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Suggestionspayload),
      });
      const data = await response.json();
      if (text !== data.response) {
        setInterviewQuestions((prev) => [...prev, { question: text, answer: data.response }]);
      }
      setResponseLoading(false);
      setMessage("");
    } catch (error) {
      console.error("gpt first response Error:", error);
      setResponseLoading(false);
      setSendError("Something went wrong while sending your message. Please try again.");
      toast.error("Something went wrong while sending your message.");
    }
  };

  /* ---------- streaming send (parsing carried over verbatim from legacy) ---------- */
  const handleSend = async (text) => {
    if (text === "") return;
    setResponseLoading(true);
    setSendError("");
    setMessage("");
    setVoiceSpeech(false);
    setVoiceText("");
    let streamResponse = "";
    let correctedText = text;

    if (text.trim() === "") return;

    const newQuestion = { question: text, answer: null };
    setInterviewQuestions((prevQuestions) => {
      turnIndexRef.current = prevQuestions.length; // this turn's real slot (append)
      return [...prevQuestions, newQuestion];
    });

    const payload = {
      transcribed_text: text,
      resume_load: true,
      email_id: userEmail,
      document_name: fileResume,
      session_id: GPTsession_id,
    };

    try {
      const response = await fetch(GPT_STREAM_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.body) throw new Error("ReadableStream not supported");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);

        if (chunk.includes('"chunk"')) {
          const matches = chunk.match(/"chunk":\s*"([^"]*)"/g);
          if (matches) {
            const filteredChunks = matches.map((match) =>
              match
                .replace(/"chunk":\s*"/, "")
                .replace(/"$/, "")
                .replace(/\\}/g, "")
                .replace(/}$/, "")
                .replace(/\\/g, "")
            );
            if (filteredChunks.length > 0) {
              let streamText = filteredChunks.join("").trim();
              streamText = beautifyStreamText(streamText);
              streamResponse += streamText;
              const newAnswer = { question: text, answer: normalizeReply(streamResponse) };
              setInterviewQuestions((prevQuestions) => {
                const updatedQuestions = [...prevQuestions];
                updatedQuestions[turnIndexRef.current] = newAnswer;
                return updatedQuestions;
              });
              setTimeout(() => scrollToBottom(false, "smooth"), 100);
            }
          }
        }

        if (chunk.includes('"corrected_text"')) {
          const match = chunk.match(/"corrected_text":\s*"([^"]*)"/);
          if (match && match[1]) {
            correctedText = match[1].replace(/\\/g, "");
            const newAnswer = { question: correctedText, answer: normalizeReply(streamResponse) };
            setInterviewQuestions((prevQuestions) => {
              const updatedQuestions = [...prevQuestions];
              updatedQuestions[turnIndexRef.current] = newAnswer;
              return updatedQuestions;
            });
          }
        }

        if (chunk.includes('"gpt_response"')) {
          const match = chunk.match(/"gpt_response":\s*"([^"]*)"/);
          if (match && match[1]) {
            const cleanedResponse = normalizeReply(match[1].replace(/\\/g, ""));
            const newAnswer = { question: correctedText, answer: cleanedResponse };
            setInterviewQuestions((prevQuestions) => {
              const updatedQuestions = [...prevQuestions];
              updatedQuestions[turnIndexRef.current] = newAnswer;
              return updatedQuestions;
            });
            setTimeout(scrollToBottom, 100);
          }
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setSendError("Something went wrong while sending your message. Please try again.");
      toast.error("Something went wrong while sending your message.");
    } finally {
      setResponseLoading(false);
    }
  };

  /* auto co-pilot: final transcripts are sent automatically (legacy behaviour) */
  useEffect(() => {
    if (autoPilotShown) {
      handleSend(speechText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speechText]);

  /* new turn added → force an instant jump to it; in-place streaming updates →
     smooth follow, only while the user is already near the bottom */
  useEffect(() => {
    const grew = interviewQuestions.length > prevTurnCountRef.current;
    prevTurnCountRef.current = interviewQuestions.length;
    if (grew) {
      stickToBottomRef.current = true;
      scrollToBottom(true, "auto");
    } else {
      scrollToBottom(false, "smooth");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewQuestions, voiceSpeech]);

  /* NOTE: the legacy "prune No need for any answer." effect used to live here and
     popped the LAST turn whenever any turn had that answer — which silently deleted
     the user's own typed/spoken answer (Pavithra Mock #1). It's gone: normalizeReply
     now turns that backend response into a useful coach line, so nothing to prune. */

  /* manual co-pilot transcript → message box (legacy behaviour) */
  useEffect(() => {
    setMessage(transcript);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript, isListening]);

  /* ---------- manual co-pilot (carried over from legacy toggleListening) ---------- */
  const toggleListening = () => {
    if (isListening) {
      SpeechRecognition.stopListening();
      setIsListening(false);
      if (transcript.trim() !== "") {
        setMessage(transcript);
        handleSend(transcript);
        resetTranscript();
      }
      /* release the ACTUAL meter stream we're holding (the old code opened a
         throwaway new stream and stopped that instead, leaking the real one) */
      if (micMeterStreamRef.current) {
        micMeterStreamRef.current.getTracks().forEach((track) => track.stop());
        micMeterStreamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
        analyserRef.current = null;
      }
    } else {
      SpeechRecognition.startListening({ continuous: true, language: "en-US" });
      /* manual push-to-talk starts immediately — the always-visible status badge
         replaces the old blocking "speak now" dialog (best-UX: no second click) */
      if (!audioContextRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioCtx();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            /* keep a handle so the mic can actually be released later — without
               this the meter stream leaked and kept the mic "in use" after
               leaving the page (held the device from other pages) */
            micMeterStreamRef.current = stream;
            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
          })
          .catch(() => {});
      }
      setIsListening(true);
      setTimeout(() => {
        if (transcript.trim() !== "") {
          handleSend(transcript);
          resetTranscript();
          setMessage("");
        }
      }, 15000);
    }
  };

  function mCopilotOpen() {
    /* one tap arms + starts push-to-talk (mic permission is the only real gate) */
    setManualCoPilot(true);
    setCopilotShown(true);
    setSpeakInitialise(false);
    toggleListening();
  }

  function mCopilotClose() {
    if (isListening) {
      setManualCoPilot(false);
      toggleListening();
      resetTranscript();
      setTimeout(() => {
        setMessage("");
        resetTranscript();
      }, 1000);
    }
  }

  function autoPSpeak() {
    setSpeakInitialise(false);
    setAutoPilotShown(true);
    setHideQuestion(false);
    setVoiceText("");
  }

  /* Spacebar = tap-to-toggle push-to-talk. Works as soon as the interview
     session is ready (or after any manual click), so it no longer needs a
     first "Manual" click to wake up. Never hijacks Space while typing, when
     voice is unsupported, or while the hands-free auto co-pilot is running. */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;
      const tag = event.target.tagName;
      if (tag === "TEXTAREA" || tag === "INPUT" || event.target.isContentEditable) return;
      if (!speechSupported || autoListening) return;
      if (!GPTsession_id && !copilotShown) return; // only once in an interview
      event.preventDefault();
      if (isListening) {
        mCopilotClose();
        resetTranscript();
      } else {
        mCopilotOpen();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListening, copilotShown, transcript, GPTsession_id, autoListening, speechSupported]);

  /* ---------- auto co-pilot (carried over from legacy AutoLanguageDetector) ---------- */
  useEffect(() => {
    if (!window.webkitSpeechRecognition) return undefined;
    const instances = [];
    AUTO_LANGS.forEach((code) => {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = code;
      recognition.onresult = (event) => {
        const result = event.results[event.results.length - 1];
        const t = result[0].transcript;
        if (result.isFinal) {
          setSpeechText(t);
          setMessage("");
        }
        setVoiceText(t);
      };
      instances.push(recognition);
    });
    recogRef.current = instances;
    return () => {
      instances.forEach((r) => {
        try {
          r.stop();
        } catch (e) {
          /* noop */
        }
      });
    };
  }, []);

  const stopAuto = () => {
    recogRef.current.forEach((r) => {
      try {
        r.stop();
      } catch (e) {
        /* noop */
      }
    });
    setAutoListening(false);
  };

  const startAuto = () => {
    stopAuto();
    recogRef.current.forEach((r) => {
      try {
        r.start();
      } catch (e) {
        /* noop */
      }
    });
    setAutoListening(true);
  };

  useEffect(() => {
    if (autoListening) speakNowShower();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoListening]);

  useEffect(() => {
    if (autoListening) setVoiceSpeech(true);
    else setVoiceSpeech(false);
  }, [voiceText, autoListening]);

  /* recording timer (surrounding chrome only) */
  const recording = autoListening || isListening;
  useEffect(() => {
    if (!recording) {
      setRecSeconds(0);
      return undefined;
    }
    const t = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  /* safety net the legacy page lacked: on navigate-away, fully release EVERY
     mic consumer so the device isn't left "in use" on other pages —
     manual recognizer, both auto-co-pilot recognizers, the meter stream, and
     the audio context. (A leaked stream here held the mic/camera app-wide.) */
  useEffect(
    () => () => {
      try {
        SpeechRecognition.stopListening();
      } catch (e) {
        /* noop */
      }
      try {
        (recogRef.current || []).forEach((r) => {
          try {
            r.stop();
          } catch (e) {
            /* noop */
          }
        });
      } catch (e) {
        /* noop */
      }
      if (micMeterStreamRef.current) {
        try {
          micMeterStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch (e) {
          /* noop */
        }
        micMeterStreamRef.current = null;
      }
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (e) {
          /* noop */
        }
        audioContextRef.current = null;
        analyserRef.current = null;
      }
    },
    []
  );

  /* ---------- full-screen toggle (pure presentation) ---------- */
  const toggleExpanded = () => {
    setExpanded((v) => !v);
    /* the thread's height changes with the layout — re-pin to bottom if the
       user was following it (same container ref, so auto-scroll carries over) */
    setTimeout(() => scrollToBottom(false, "auto"), 0);
  };

  /* Esc exits full-screen; listener only attached while expanded */
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  /* ---------- code-aware response rendering (carried over from legacy) ---------- */
  const renderResponse = (response) => {
    if (!response) return null;
    const cleanedResponse = response.trim().endsWith("}") ? response.trim().slice(0, -1).trim() : response.trim();
    const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = codeBlockRegex.exec(cleanedResponse)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: "text", content: cleanedResponse.slice(lastIndex, match.index) });
      }
      parts.push({ type: "code", language: match[1] || "text", content: match[2] });
      lastIndex = codeBlockRegex.lastIndex;
    }
    if (lastIndex < cleanedResponse.length) {
      parts.push({ type: "text", content: cleanedResponse.slice(lastIndex) });
    }
    return parts.map((part, index) => {
      if (part.type === "code") {
        return (
          <SyntaxHighlighter key={index} language={part.language} style={coy}>
            {part.content}
          </SyntaxHighlighter>
        );
      }
      return <span key={index}>{part.content}</span>;
    });
  };

  const fmtTimer = `${String(Math.floor(recSeconds / 60)).padStart(2, "0")}:${String(recSeconds % 60).padStart(
    2,
    "0"
  )}`;

  const status = recording ? "live" : responseLoading ? "processing" : "idle";
  /* the live caption should show only what you're saying RIGHT NOW — not the
     whole accumulating transcript, which grew this panel and pushed the answer
     below. Show just the tail (most recent ~90 chars), starting at a word. The
     full answer is still captured and sent; this only trims the on-screen live
     preview. */
  const liveTail = (txt) => {
    const s = String(txt || "").replace(/\s+/g, " ").trim();
    if (s.length <= 90) return s;
    return "…" + s.slice(-90).replace(/^\S*\s/, "");
  };
  const stageCaption = recording
    ? !hideQuestion && voiceSpeech && voiceText
      ? liveTail(voiceText)
      : isListening && transcript
      ? liveTail(transcript)
      : "Listening…"
    : responseLoading
    ? "Thinking…"
    : "Ask your coach an interview question to begin — speak or type.";

  /* ============================ gated / empty states ============================ */
  if (blocked) {
    return (
      <div className="ld-glass mi-upgrade">
        <div className="mi-upgrade-glow" aria-hidden="true" />
        <span className="mi-upgrade-lock">
          <LuLock size={20} />
        </span>
        <h3 className="t-h3 mt-3">Mock interview is a Pro feature</h3>
        <p className="t-sm mt-1 mi-upgrade-sub">
          Upgrade to Pro to practice unlimited AI-powered mock interviews tailored to your resume.
        </p>
        <div className="mt-3">
          <Btn
            variant="mint"
            className="btn-pill"
            onClick={() => {
              dispatch(setShowPro(true));
              navigate("/subscription");
            }}
          >
            <LuSparkles size={16} /> Upgrade to Pro
          </Btn>
        </div>
      </div>
    );
  }

  if (!fileResume && !loaderAnimation) {
    return (
      <div className="ld-empty">
        <span className="ld-empty-icon">
          <LuFileText size={20} />
        </span>
        <div className="ld-empty-title">Upload your resume first</div>
        <p className="ld-empty-sub">
          Your mock interview is personalised from your resume. Upload it once and come back here.
        </p>
        <div className="mt-2">
          <Btn variant="mint" className="btn-pill" to="/resume-upload">
            Upload resume
          </Btn>
        </div>
      </div>
    );
  }

  /* ============================ main render ============================ */
  return (
    <div className={`mi-layout${expanded ? " mi-full" : ""}`}>
      <div className="mi-main stack-3">
        {/* ---------- session card (idle / recording / processing) ---------- */}
        <section className="ld-glass mi-panel">
          <div className="mi-topline">
            <span className={`mi-status ${status}`}>
              <LuRadio size={13} className={status === "live" ? "mi-blink" : undefined} />
              {status === "live" ? "Live" : status === "processing" ? "Processing" : "Idle"}
            </span>
            <span className="mi-timer">{fmtTimer}</span>
            <div className="mi-actions">
              {/* two clear answer modes: hands-free auto vs push-to-talk */}
              {autoListening ? (
                <Btn variant="danger" className="btn-pill" size="sm" onClick={stopAuto} title="Stop hands-free listening">
                  <LuSquare size={14} /> Stop auto
                </Btn>
              ) : (
                <Btn
                  variant="mint"
                  className="btn-pill"
                  size="sm"
                  onClick={startAuto}
                  disabled={!speechSupported || isListening}
                  title={
                    speechSupported
                      ? "Hands-free: the AI listens and sends your answers automatically"
                      : "Voice input is not supported in this browser"
                  }
                >
                  <LuMic size={14} /> Auto mode
                </Btn>
              )}
              {browserSupportsSpeechRecognition ? (
                manualCoPilot && isListening ? (
                  <Btn
                    variant="danger-soft"
                    className="btn-pill"
                    size="sm"
                    onClick={mCopilotClose}
                    title="Stop and send your answer (or press Space)"
                  >
                    <LuMicOff size={14} /> Stop &amp; send
                  </Btn>
                ) : (
                  <Btn
                    variant="ghost"
                    className="btn-pill"
                    size="sm"
                    onClick={mCopilotOpen}
                    disabled={!speechSupported || autoListening}
                    title="Manual mode — tap to talk, tap again to send (or press Space)"
                  >
                    <LuMic size={14} /> Manual mode
                  </Btn>
                )
              ) : null}
              <span className="mi-actions-util">
                <button
                  type="button"
                  className="mi-iconbtn"
                  onClick={clearChats}
                  title="Clear conversation"
                  aria-label="Clear conversation"
                >
                  <LuRefreshCw size={15} />
                </button>
                <button
                  type="button"
                  className="mi-iconbtn"
                  onClick={toggleExpanded}
                  aria-label={expanded ? "Exit full screen" : "Full screen"}
                  title={expanded ? "Exit full screen (Esc)" : "Full screen"}
                >
                  {expanded ? <LuMinimize2 size={15} /> : <LuMaximize2 size={15} />}
                </button>
              </span>
            </div>
          </div>

          <div className={`mi-stage${recording ? "" : " idle"}`}>
            <div className={`mi-wave${recording ? " live" : ""}`} aria-hidden="true">
              {Array.from({ length: 14 }).map((_, i) => (
                <span
                  key={i}
                  style={{
                    height: recording ? `${8 + ((i * 37) % 18)}px` : "6px",
                    animationDelay: `${i * 70}ms`,
                  }}
                />
              ))}
            </div>
            <p className="mi-caption">{stageCaption}</p>
          </div>
        </section>

        {/* ---------- transcript ---------- */}
        <section className="ld-glass mi-panel">
          <h3 className="t-h3">Transcript</h3>
          <div className="mi-thread" ref={messageContainerRef} onScroll={handleThreadScroll}>
            {loaderAnimation ? (
              <>
                <Skel h={44} w="55%" />
                <Skel h={70} w="75%" />
                <Skel h={44} w="45%" style={{ alignSelf: "flex-end" }} />
              </>
            ) : (
              <>
                {sessionWarming && !sessionError ? (
                  <Note tone="info">
                    <Spinner />
                    <span className="grow">
                      Warming up your interview coach — the first load after a quiet period can take up to a
                      minute…
                    </span>
                  </Note>
                ) : null}

                {sessionError ? (
                  <Note tone="danger">
                    <span className="grow">{sessionError}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSessionError("");
                        setLoaderAnimation(true);
                        loadSession();
                        setTimeout(() => setLoaderAnimation(false), 3500);
                      }}
                      style={{ fontWeight: 600, textDecoration: "underline" }}
                    >
                      Retry
                    </button>
                  </Note>
                ) : null}

                {interviewQuestions.length === 0 && !sessionError ? (
                  <>
                    <div className="ld-empty mi-empty">
                      <span className="ld-empty-icon">
                        <LuMic size={18} />
                      </span>
                      <div className="ld-empty-title">Your interview coach is ready</div>
                      <p className="ld-empty-sub">
                        This is your live interview co-pilot: ask it any interview question and it answers using
                        your resume — keep it beside a real call, or use it to rehearse. Speak with <b>Auto</b> /
                        <b> Manual</b> mode, or just type a question below.
                      </p>
                    </div>
                    <DesktopAppCard />
                  </>
                ) : null}

                {interviewQuestions.map((item, index) => (
                  <React.Fragment key={index}>
                    {item?.question && item.question.trim() ? (
                      <div className="mi-turn user">
                        <p className="mi-turn-role">You</p>
                        <span style={{ whiteSpace: "pre-wrap" }}>{item.question}</span>
                      </div>
                    ) : null}
                    <div className="mi-turn">
                      <p className="mi-turn-role">Coach</p>
                      {item?.answer == null || item?.answer === "" ? (
                        <span className="row" style={{ gap: 8 }}>
                          {/* awaiting the AI reply — "Thinking", not "Transcribing"
                              (which is wrong for typed answers; Pavithra Mock #3) */}
                          <Spinner /> Thinking…
                        </span>
                      ) : (
                        <div style={{ whiteSpace: "pre-wrap" }}>{renderResponse(item?.answer)}</div>
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>

          {sendError ? <Note tone="danger">{sendError}</Note> : null}

          <div className="mi-composer">
            <textarea
              className="input grow"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type an interview question (or your own answer)…"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(message);
                }
              }}
            />
            <Btn
              variant="mint"
              className="btn-pill mi-send"
              onClick={() => handleSend(message)}
              disabled={responseLoading}
              aria-label="Send answer"
              title="Send answer"
            >
              <LuSend size={16} />
            </Btn>
          </div>
          <p className="mi-tip">
            <b>Auto mode</b> listens hands-free and sends when you pause. <b>Manual mode</b>: press Space (or click) to
            talk, press again to send.
          </p>
        </section>
      </div>

      {/* ---------- aside ---------- */}
      <aside className="mi-aside stack-3">
        <section className={`ld-glass mi-panel${aiChatSuggestions ? " mi-fresh" : ""}`}>
          <h3 className="t-h3 row" style={{ gap: 8 }}>
            <LuSparkles size={15} style={{ color: "var(--accent)" }} /> AI suggestions
          </h3>
          <ul className="mi-suggest">
            {SUGGESTIONS.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onClick={() => chatSuggestionsClick(s)}
                  disabled={responseLoading || loaderAnimation}
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="ld-glass mi-panel">
          <h3 className="t-h3 row" style={{ gap: 8 }}>
            <LuBot size={15} style={{ color: "var(--accent)" }} /> Session status
          </h3>
          <div className="stack-2 mt-2">
            <div className="mi-row">
              <span>Auto mode</span>
              <b>{autoListening ? "Listening…" : "Off"}</b>
            </div>
            <div className="mi-row">
              <span>Manual mode</span>
              <b>{isListening ? "Listening…" : "Ready"}</b>
            </div>
            <div className="mi-row">
              <span>Session</span>
              <b>{GPTsession_id ? "Ready" : "Warming up…"}</b>
            </div>
          </div>
          <span className="mi-note-badge">
            {speechSupported ? (
              <>
                <LuMic size={13} /> Press Space to talk, press again to send
              </>
            ) : (
              <>
                <LuMicOff size={13} /> Voice input unavailable in this browser
              </>
            )}
          </span>
        </section>
      </aside>

      {/* ---------- hands-free (auto) co-pilot explainer ---------- */}
      <Dialog open={speakInitialise} onClose={() => setSpeakInitialise(false)}>
        <div className="row mb-2" style={{ gap: 10 }}>
          <span className="mi-pulse">
            <LuMic size={15} />
          </span>
          <div className="t-h3">Hands-free mode is on</div>
        </div>
        <p className="t-sm mb-3">
          Just speak naturally and the AI will listen and reply on its own, no keys needed. If it
          picks up background noise or cuts you off, switch to Push to talk for full control: tap
          Space to talk, tap again to send.
        </p>
        <Btn
          variant="mint"
          className="btn-pill btn-full"
          onClick={() => {
            autoPSpeak();
          }}
        >
          Got it
        </Btn>
      </Dialog>
    </div>
  );
}
